import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfigService } from '../../shared-services/config.service';

type FormFieldOption = string | { label: string; value: string };

interface FormFieldConfig {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'email' | 'tel' | 'textarea';
  placeholder?: string;
  required?: boolean;
  defaultValue?: any;
  colSpan?: number;
  options?: FormFieldOption[];
  optionsApiUrl?: string;
}

interface FormStepConfig {
  number?: number | string;
  label: string;
  active?: boolean;
}

interface ConfigurableFormConfig {
  title?: string;
  description?: string;
  headerColor?: string;
  height?: string;
  alignment?: 'left' | 'center' | 'right';
  steps?: FormStepConfig[];
  fields?: FormFieldConfig[];
  submitLabel?: string;
  method?: string;
  layout?: {
    columns?: number;
    gap?: string;
  };
}

@Component({
  selector: 'app-configurable-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './configurable-form.component.html',
  styleUrl: './configurable-form.component.scss',
})
export class ConfigurableFormComponent implements OnInit, OnChanges {
  @Input() data?: ConfigurableFormConfig & { apiUrl?: string };
  @Output() submitted = new EventEmitter<Record<string, any>>();

  form = new FormGroup({});
  fields: FormFieldConfig[] = [];
  steps: FormStepConfig[] = [];
  title = '';
  description = '';
  headerColor = '';
  height = '';
  alignment: 'left' | 'center' | 'right' = 'left';
  submitLabel = 'Submit';
  isLoading = false;

  constructor(private configService: ConfigService) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data']?.isFirstChange()) {
      this.initializeForm();
    }
  }

  get layoutColumns(): number {
    return this.data?.layout?.columns || 2;
  }

  get layoutGap(): string {
    return this.data?.layout?.gap || '24px';
  }

  trackByKey(_: number, field: FormFieldConfig): string {
    return field.key;
  }

  isFieldInvalid(field: FormFieldConfig): boolean {
    const control = this.form.get(field.key);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  getOptionLabel(option: FormFieldOption): string {
    return typeof option === 'string' ? option : option.label;
  }

  getOptionValue(option: FormFieldOption): string {
    return typeof option === 'string' ? option : option.value;
  }

  getFieldType(field: FormFieldConfig): string {
    return field.type === 'select' || field.type === 'textarea' ? 'text' : field.type;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitted.emit(this.form.getRawValue());
  }

  private initializeForm(): void {
    if (!this.data) {
      return;
    }

    if (this.data.apiUrl) {
      this.isLoading = true;
      const method = this.data?.method || 'get';
      this.configService.getConfig(this.data.apiUrl, this.data, undefined, method).subscribe({
        next: (config: any) => {
          this.applyConfig(this.mergeConfig(config as ConfigurableFormConfig));
          this.isLoading = false;
        },
        error: () => {
          this.applyConfig(this.mergeConfig());
          this.isLoading = false;
        },
      });
      return;
    }

    this.applyConfig(this.mergeConfig());
  }

  private mergeConfig(apiConfig?: ConfigurableFormConfig): ConfigurableFormConfig {
    const dataConfig = this.data || {};
    return {
      ...apiConfig,
      ...dataConfig,
      fields: dataConfig.fields || apiConfig?.fields || [],
      steps: dataConfig.steps || apiConfig?.steps || [],
    };
  }

  private applyConfig(config: ConfigurableFormConfig): void {
    this.title = config.title || '';
    this.description = config.description || '';
    this.headerColor = config.headerColor || '';
    this.height = config.height || '';
    this.alignment = config.alignment || 'left';
    this.fields = config.fields || [];
    this.steps = config.steps || [];
    this.submitLabel = config.submitLabel || 'Submit';
    this.buildFormGroup();
    this.loadFieldOptions();
  }

  private buildFormGroup(): void {
    const group: { [key: string]: FormControl } = {};

    this.fields.forEach((field) => {
      const validators = field.required ? [Validators.required] : [];
      const defaultValue = field.defaultValue ?? (field.type === 'select' ? '' : '');
      group[field.key] = new FormControl(defaultValue, validators);
    });

    this.form = new FormGroup(group);
  }

  private loadFieldOptions(): void {
    this.fields.forEach((field) => {
      if (!field.optionsApiUrl || (field.options && field.options.length > 0)) {
        return;
      }

      this.configService.getConfig(field.optionsApiUrl, undefined, undefined, 'get').subscribe({
        next: (response: any) => {
          const options = Array.isArray(response) ? response : response?.data;
          if (Array.isArray(options)) {
            field.options = options;
          }
        },
        error: () => {
          field.options = field.options || [];
        },
      });
    });
  }
}
